import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { Flag, Check, X, ExternalLink, Inbox, Trash2 } from "lucide-react";

export default function AdminReports() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("open");
  const [data, setData] = useState({ reports: [], counts: {} });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/admin/reports?status=${tab}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ reports: [], counts: {} }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [tab, user]);

  if (authLoading) return <div className="brutal-card p-10 text-center font-mono-display uppercase tracking-widest">Checking the hatch...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const resolve = async (id, status) => {
    const resolution = window.prompt(`Note (optional) for ${status}:`, "") ?? "";
    try {
      await api.patch(`/admin/reports/${id}`, { status, resolution });
      toast.success(`Report ${status}.`);
      load();
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Session expired. Sign in again." : "Action failed.");
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm("Delete the reported section?")) return;
    try {
      await api.delete(`/sections/${sectionId}`);
      toast.success("Section deleted; report auto-resolved.");
      load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-reports-page">
      <Toaster position="bottom-right" />
      <AdminNav active="reports" />
      <div className="mb-5">
        <div className="font-mono-display text-xs uppercase tracking-[0.3em] text-pink-600 mb-1">Moderation</div>
        <h1 className="font-mono-display text-3xl font-bold tracking-tight border-b-4 border-slate-900 pb-2 uppercase">Report Queue</h1>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap" data-testid="report-tabs">
        {["open", "resolved", "dismissed"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`brutal-btn ${tab === t ? "primary" : ""}`} data-testid={`report-tab-${t}`}>
            <Inbox size={14} strokeWidth={3} /> {t} ({data.counts?.[t] ?? 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="brutal-card p-8 text-center font-mono-display uppercase tracking-widest">Loading...</div>
      ) : data.reports.length === 0 ? (
        <div className="brutal-card p-8 text-center" data-testid="reports-empty">
          <Flag size={28} strokeWidth={2.5} className="mx-auto mb-2 text-slate-400" />
          <div className="font-mono-display uppercase tracking-widest text-slate-700">&gt; No {tab} reports</div>
        </div>
      ) : (
        <ul className="space-y-4">
          {data.reports.map((r) => (
            <li key={r.id} className="brutal-card overflow-hidden" data-testid={`report-${r.id}`}>
              <div className="bg-red-500 text-white px-4 py-2 flex flex-wrap items-center gap-3 font-mono-display text-xs uppercase tracking-widest">
                <Flag size={12} strokeWidth={3} />
                <span>{r.status}</span>
                <span className="text-red-200">·</span>
                <span>{r.reporter_name}</span>
                <span className="ml-auto normal-case tracking-normal text-[11px] text-red-100">
                  {new Date(r.created_at).toLocaleString()}
                </span>
              </div>
              <div className="p-5">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
                  S{r.season}E{String(r.episode_number).padStart(2, "0")} · {r.episode_title} {!r.section_exists && "· (section deleted)"}
                </div>
                <div className="font-bold text-lg mb-1">{r.headline_snapshot}</div>
                <div className="text-sm text-slate-700 mb-3 italic">"{r.body_snapshot}"</div>
                <div className="bg-yellow-100 brutal-border p-3 mb-3">
                  <div className="font-mono-display text-[10px] uppercase tracking-widest text-slate-600 mb-1">Reason</div>
                  <div className="text-sm text-slate-900">{r.reason}</div>
                </div>
                {r.resolution && (
                  <div className="text-xs text-slate-600 mb-3">Note: {r.resolution} — by {r.resolved_by}</div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Link to={`/episodes/${r.episode_id}`} className="brutal-btn !py-1 text-xs" data-testid={`report-view-${r.id}`}>
                    <ExternalLink size={12} strokeWidth={3} /> View Episode
                  </Link>
                  {tab === "open" && r.section_exists && (
                    <button onClick={() => deleteSection(r.section_id)} className="brutal-btn !py-1 text-xs" style={{ background: "#ef4444", color: "#fff" }} data-testid={`report-delete-${r.id}`}>
                      <Trash2 size={12} strokeWidth={3} /> Delete Section
                    </button>
                  )}
                  {tab === "open" && (
                    <>
                      <button onClick={() => resolve(r.id, "resolved")} className="brutal-btn primary !py-1 text-xs" data-testid={`report-resolve-${r.id}`}>
                        <Check size={12} strokeWidth={3} /> Resolve
                      </button>
                      <button onClick={() => resolve(r.id, "dismissed")} className="brutal-btn !py-1 text-xs" data-testid={`report-dismiss-${r.id}`}>
                        <X size={12} strokeWidth={3} /> Dismiss
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminNav({ active }) {
  return (
    <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-nav">
      <Link to="/admin/reports" className={`brutal-btn ${active === "reports" ? "primary" : ""}`} data-testid="admin-nav-reports">
        <Flag size={14} strokeWidth={3} /> Reports
      </Link>
      <Link to="/admin/audit" className={`brutal-btn ${active === "audit" ? "primary" : ""}`} data-testid="admin-nav-audit">
        <Inbox size={14} strokeWidth={3} /> Audit Log
      </Link>
    </div>
  );
}
